import { RefObject, useEffect, useRef, useState } from "react";
import firebase from "store/firebase";
import "firebase/storage";
import { Beer } from "store/reducers/beers/reducer";

/**
 * Session-wide cache of resolved bottle image URLs, keyed by beer id. Beer
 * ids repeat across events (one shared catalog), so a URL resolved for one
 * event's card is reused everywhere — including re-visits.
 */
const urlCache = new Map<string, Promise<string | null>>();

const resolveImageUrl = (beerId: string): Promise<string | null> => {
  let cached = urlCache.get(beerId);
  if (!cached) {
    cached = firebase
      .storage()
      .ref(`images/beers/${beerId}.png`)
      .getDownloadURL()
      .catch(() => null);
    urlCache.set(beerId, cached);
  }
  return cached;
};

/**
 * Lazily resolves a beer's bottle image URL the first time its card scrolls
 * near the viewport, instead of fetching every image up-front when the tab
 * opens. Returns a ref to attach to the card element plus the resolved URL
 * (null until it lands / when there is none).
 *
 * A URL already carried on the catalog doc (`beer.imageUrl`) is used as-is
 * with no Storage request. Without IntersectionObserver support the hook
 * simply leaves the placeholder in place.
 */
const useBeerImageUrl = (
  beer: Beer
): [RefObject<HTMLDivElement>, string | null] => {
  const ref = useRef<HTMLDivElement>(null);
  const [url, setUrl] = useState<string | null>(beer.imageUrl ?? null);

  useEffect(() => {
    if (beer.imageUrl) {
      setUrl(beer.imageUrl);
      return;
    }
    const node = ref.current;
    if (!beer.id || !node || typeof IntersectionObserver === "undefined") {
      return;
    }
    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        resolveImageUrl(beer.id).then((resolved) => {
          if (!cancelled && resolved) setUrl(resolved);
        });
      },
      // Start fetching a little before the card actually becomes visible so
      // the image is usually there by the time the user scrolls to it.
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [beer.id, beer.imageUrl]);

  return [ref, url];
};

export default useBeerImageUrl;
